import { Card, Flex, Grid, Text } from '@servicetitan/anvil2';
import { DataItem, firstNonBlankValue, formatCurrency } from '@servicetitan/financing-utils-shared';
import { DecisionDataStore } from '../stores/DecisionDataStore';
import {
    PrequalificationResponseStore,
    SetupDataStore,
    useProviderConfig,
} from '@servicetitan/financing-workflow-shared';

export interface SoftAppDetailsProps {
    hideDetails?: boolean;
}

export const SoftAppDetails = ({ hideDetails = false }: SoftAppDetailsProps) => {
    const { decisionData } = DecisionDataStore.useStore();
    const { prequalificationResponse } = PrequalificationResponseStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { termMultiplier } = useProviderConfig();
    const { approvedAmount, term, rate, description } = decisionData ?? {};
    const date = new Date().toLocaleDateString();

    // A soft prequal decision arrives with its externalPrequalId already copied onto
    // externalLoanId (see useFinancingPrequalSubmission), so which field holds the id cannot
    // name it. Nothing clears the prequal response when a soft prequal continues into a hard
    // app, so once the hard app has taken over the leftover prequal id must not win.
    const isSoftPrequal =
        !!prequalificationResponse?.externalPrequalId?.trim() &&
        !setupData?.hasTransitionedToHardApp;
    const applicationIdLabel = isSoftPrequal ? 'Pre-Qualification ID' : 'Application ID';
    const applicationId = isSoftPrequal
        ? firstNonBlankValue(
              prequalificationResponse?.externalPrequalId,
              decisionData?.externalLoanId,
          )
        : firstNonBlankValue(
              decisionData?.externalLoanId,
              prequalificationResponse?.externalPrequalId,
          );

    return (
        <Card flexDirection="column" style={{ maxWidth: '845px', width: '100%' }}>
            <Flex gap="3" direction="column">
                <Text variant="headline" el="h2" size="small">
                    Details
                </Text>
                <Grid rowGap="4" templateColumns="repeat(auto-fit, minmax(255px, 1fr))">
                    {applicationId && <DataItem label={applicationIdLabel} text={applicationId} />}
                    <DataItem label="Date" text={date} />
                    {!hideDetails && (
                        <>
                            <DataItem
                                label="Loan Amount"
                                text={formatCurrency(approvedAmount ?? 0)}
                            />
                            <DataItem
                                label="Available Credit"
                                text={formatCurrency(approvedAmount ?? 0)}
                            />

                            <DataItem
                                label="Term"
                                text={
                                    term ? [term * termMultiplier, 'months'].join(' ') : undefined
                                }
                            />
                            <DataItem label="APR" text={rate?.toFixed(2) + '%'} />
                            <DataItem
                                label="Plan Description"
                                text={description ? <Text>{description}</Text> : undefined}
                            />
                        </>
                    )}
                </Grid>
            </Flex>
        </Card>
    );
};
